import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { RefreshCw, Gauge, Activity, Database, Server, DollarSign, Users, Receipt, Check, Clock } from "lucide-react";
import { AdminIspInventory } from "./AdminIspInventory";
import { AdminIspAssignments } from "./AdminIspAssignments";

export function AdminPanel() {
  const [ov, setOv] = useState(null);
  const [rows, setRows] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, u, ord] = await Promise.all([api.get("/admin/overview"), api.get("/admin/users"), api.get("/admin/orders")]);
      setOv(o.data); setRows(u.data); setOrders(ord.data);
    } catch (e) { logError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const KPIS = ov ? [
    ["GB Remaining", ov.partner.balance_gb, "text-neon", Gauge],
    ["GB Used (pool)", ov.partner.used_gb, "text-ink", Activity],
    ["GB Sold", ov.totals.gb_sold, "text-ink", Database],
    ["ISP IPs Sold", ov.totals.isp_ips_sold, "text-ink", Server],
    ["Revenue", `$${ov.totals.revenue.toFixed(2)}`, "text-neon", DollarSign],
    ["Users", ov.totals.users, "text-ink", Users],
  ] : [];
  const renderUserRows = () => {
    if (loading) return <tr><td colSpan={9} className="p-8 text-center text-zinc-400">Loading…</td></tr>;
    if (rows.length === 0) return <tr><td colSpan={9} className="p-8 text-center text-zinc-400">No users yet.</td></tr>;
    return rows.map((r) => (
      <tr key={r.user_id} className="border-b border-[#F2F8F7]" data-testid={`admin-user-row-${r.email}`}>
        <td className="p-4"><div className="font-medium text-ink flex items-center gap-2">{r.name || "—"}{r.role === "admin" && <span className="text-[10px] text-neon border border-neon/30 rounded px-1.5 py-0.5">admin</span>}</div><div className="text-xs text-zinc-400">{r.email}</div></td>
        <td className="p-4 text-zinc-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
        <td className="p-4 text-zinc-500 capitalize">{r.auth_provider}</td>
        <td className="p-4 font-mono text-neon">{r.proxy_username || "—"}</td>
        <td className="p-4 font-mono">{r.gb_purchased} GB</td>
        <td className="p-4 font-mono">{r.bandwidth_used_gb ?? "—"}</td>
        <td className="p-4 font-mono">{r.bandwidth_available_gb ?? "—"}</td>
        <td className="p-4 font-mono">{r.isp_purchased}</td>
        <td className="p-4 font-mono text-ink">${(r.total_spent || 0).toFixed(2)}</td>
      </tr>
    ));
  };
  const renderOrderRows = () => {
    if (loading) return <tr><td colSpan={5} className="p-8 text-center text-zinc-400">Loading…</td></tr>;
    if (orders.length === 0) return <tr><td colSpan={5} className="p-8 text-center text-zinc-400">No paid orders yet.</td></tr>;
    return orders.map((o) => (
      <tr key={o.session_id} className="border-b border-[#F2F8F7]" data-testid={`admin-order-row-${o.session_id}`}>
        <td className="p-4 text-zinc-500 whitespace-nowrap">{o.created_at ? new Date(o.created_at).toLocaleString() : "—"}</td>
        <td className="p-4"><div className="font-mono text-neon">{o.username || "—"}</div><div className="text-xs text-zinc-400">{o.email || "—"}</div></td>
        <td className="p-4"><span className="text-ink font-medium">{o.product}</span> <span className="text-zinc-400">· {o.quantity} {o.unit}</span></td>
        <td className="p-4 font-mono text-ink">${o.amount.toFixed(2)} <span className="text-zinc-400 text-xs">{o.currency}</span></td>
        <td className="p-4">{o.provisioned
          ? <span className="inline-flex items-center gap-1 text-[11px] text-neon border border-neon/30 rounded-full px-2 py-0.5"><Check className="w-3 h-3" /> Provisioned</span>
          : <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 border border-amber-300 rounded-full px-2 py-0.5"><Clock className="w-3 h-3" /> Pending</span>}</td>
      </tr>
    ));
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {KPIS.map(([label, val, color, Icon]) => (
          <div key={label} className="bg-white border border-[#E4EEEC] rounded-2xl p-5 shadow-sm" data-testid={`admin-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/,"")}`}>
            <div className="flex items-center justify-between mb-3"><span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{label}</span><Icon className={`w-4 h-4 ${color}`} aria-hidden="true" /></div>
            <div className={`font-head text-2xl font-extrabold ${color}`}>{val}</div>
          </div>
        ))}
      </div>
      <AdminIspInventory />
      <AdminIspAssignments />
      <div className="bg-white border border-[#E4EEEC] rounded-2xl overflow-hidden shadow-sm">
        <div className="flex items-center justify-between p-4 border-b border-[#EFF6F5]">
          <h3 className="font-head text-lg font-semibold text-ink flex items-center gap-2"><Receipt className="w-4 h-4 text-neon" aria-hidden="true" /> Recent Orders <span className="text-zinc-400 text-sm font-normal">({orders.length})</span></h3>
          <button onClick={load} className="text-sm text-zinc-500 hover:text-neon flex items-center gap-1" data-testid="admin-orders-refresh-button"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-orders-table">
            <thead><tr className="text-left text-zinc-400 font-mono text-xs uppercase border-b border-[#EFF6F5]">
              <th className="p-4">Date</th><th className="p-4">Customer</th><th className="p-4">Order</th><th className="p-4">Amount</th><th className="p-4">Status</th>
            </tr></thead>
            <tbody>{renderOrderRows()}</tbody>
          </table>
        </div>
      </div>
      <div className="bg-white border border-[#E4EEEC] rounded-2xl overflow-hidden shadow-sm">
        <div className="flex items-center justify-between p-4 border-b border-[#EFF6F5]">
          <h3 className="font-head text-lg font-semibold text-ink flex items-center gap-2"><Users className="w-4 h-4 text-neon" aria-hidden="true" /> Users <span className="text-zinc-400 text-sm font-normal">({rows.length})</span></h3>
          <button onClick={load} className="text-sm text-zinc-500 hover:text-neon flex items-center gap-1" data-testid="admin-refresh-button"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead><tr className="text-left text-zinc-400 font-mono text-xs uppercase border-b border-[#EFF6F5]">
              <th className="p-4">User</th><th className="p-4">Joined</th><th className="p-4">Login</th><th className="p-4">Proxy user</th><th className="p-4">Bought</th><th className="p-4">Used</th><th className="p-4">Left</th><th className="p-4">ISP</th><th className="p-4">Spent</th>
            </tr></thead>
            <tbody>{renderUserRows()}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
